import { useState, useEffect, useCallback } from "react";
import { useBlocker } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface UnsavedChangesGuardProps {
  hasUnsavedChanges: boolean;
  message?: string;
}

export default function UnsavedChangesGuard({ hasUnsavedChanges, message = "You have unsaved changes. Are sure you want to leave?"}: UnsavedChangesGuardProps) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [nextLocation, setNextLocation] = useState<any>(null);
  const [proceed, setProceed] = useState<null | boolean>(null);
  const [submitting, setSubmitting] = useState(false);

  // Mark when submit is happening (so guard won't trigger)
  useEffect(() => {
    const handleSubmitClick = () => setSubmitting(true);

    // watch all submit buttons
    document.querySelectorAll("button[type='submit']").forEach((btn) => {
      btn.addEventListener("click", handleSubmitClick);
    });

    return () => {
      document.querySelectorAll("button[type='submit']").forEach((btn) => {
        btn.removeEventListener("click", handleSubmitClick);
      });
    };
  }, []);


  // Block navigation if there are unsaved changes
  const blocker = useBlocker(
    hasUnsavedChanges && !showConfirmModal && !submitting
      ? ({ nextLocation }) => {
          console.log('Navigation blocked - hasUnsavedChanges:', hasUnsavedChanges, 'showConfirmModal:', showConfirmModal);
          setNextLocation(nextLocation);
          setShowConfirmModal(true);
          return true; // block for now
        }
      : false
  );

  const confirmLeave = useCallback(() => {
    setProceed(true);
    setShowConfirmModal(false);
  }, []);

  const cancelLeave = useCallback(() => {
    setProceed(false);
    setShowConfirmModal(false);
  }, []);

  // If confirmed, proceed with navigation
  useEffect(() => {
    if (proceed && nextLocation) {
      blocker.proceed?.();
      setProceed(null);
      setNextLocation(null);
    }
  }, [proceed, nextLocation, blocker]);


  // Handle tab/browser close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !submitting) {
        e.preventDefault();
        return message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, message, submitting]);

  return (
    <>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unsaved Changes</h3>
              <p className="text-gray-600 text-sm mb-6">{message}</p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelLeave}
                  className="btn-secondary flex-1 py-2 px-4"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLeave}
                  className="btn-primary-lg flex-1 py-2 px-4"
                >
                  Leave Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}